import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import sflLogo from "../assets/ui_revamp/SFL 3.png";
import sunIcon from "../assets/ui_revamp/sun.png";
import { uploadDesignerReport } from "./reportUpload";
import {
  fitImageContain,
  getPreferredOrganizationLogoUrl,
  loadImageAsDataUrl,
  loadImageDimensions,
  trimImageWhitespace,
} from "./reportBranding";

const MONTHS_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const MODULE_TYPE_MAP = {
  0: "Standard",
  1: "Premium",
  2: "Thin film",
};

const ARRAY_TYPE_MAP = {
  0: "Fixed - Open Rack",
  1: "Fixed - Roof Mounted",
  2: "1-Axis",
  3: "1-Axis Backtracking",
  4: "2-Axis",
};

const colors = {
  bg: [245, 246, 248],
  text: [30, 42, 54],
  muted: [138, 146, 154],
  mutedLight: [163, 163, 163],
  cardBorder: [219, 223, 228],
  deep: [5, 34, 51],
  deepSoft: [7, 48, 74],
  accent: [214, 208, 75],
};

const toNumber = (v) => {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string") {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
};

const fmtInt = (v) => {
  const n = toNumber(v);
  return n == null ? "N/A" : `${Math.round(n).toLocaleString()}`;
};

const fmtNum = (v, d = 1) => {
  const n = toNumber(v);
  return n == null ? "N/A" : n.toFixed(d);
};

const fmtPct = (v, d = 1) => {
  const n = toNumber(v);
  return n == null ? "N/A" : `${n.toFixed(d)}%`;
};

const fmtLatLng = (lat, lng) => {
  const latN = toNumber(lat);
  const lngN = toNumber(lng);
  if (latN == null || lngN == null) return "N/A";
  return `${latN.toFixed(2)} N , ${Math.abs(lngN).toFixed(2)} W`;
};

const pickFirstNonEmpty = (...vals) => {
  for (const v of vals) {
    if (typeof v === "string" && v.trim()) return v.trim();
    if (typeof v === "number" && Number.isFinite(v)) return String(v);
  }
  return "";
};

const nameFrom = (obj) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj.trim();
  return pickFirstNonEmpty(
    obj.name,
    obj.fullName,
    obj.full_name,
    obj.companyName,
    obj.company_name,
    obj.title
  );
};

const getStoredDesignerName = () => {
  if (typeof window === "undefined") return "";

  try {
    return window.localStorage.getItem("designerName") || "";
  } catch (error) {
    return "";
  }
};

const getDesignerName = (design) =>
  pickFirstNonEmpty(
    nameFrom(design?.designer),
    design?.designerName,
    design?.designer_name,
    nameFrom(design?.createdBy),
    design?.created_by_name,
    design?.createdByName,
    getStoredDesignerName()
  ) || "N/A";

const getOrganizationName = (design) =>
  pickFirstNonEmpty(
    nameFrom(design?.organization),
    design?.organizationName,
    design?.organization_name,
    nameFrom(design?.org),
    design?.orgName,
    design?.org_name
  ) || "N/A";

const getJobName = (design) => {
  const label = pickFirstNonEmpty(
    design?.name,
    design?.projectName,
    design?.project_name,
    design?.title
  );
  const id = pickFirstNonEmpty(design?.id, design?.uuid, design?.project_id);
  if (label && id) return `${label} - ${id}`;
  return label || id || "N/A";
};

const getAddress = (requestedLocation, currentDesign) =>
  requestedLocation || currentDesign?.address || "Address not available";

const calcAvg = (arr) => {
  if (!Array.isArray(arr) || !arr.length) return null;
  const valid = arr.map(toNumber).filter((v) => v != null);
  if (!valid.length) return null;
  return valid.reduce((a, b) => a + b, 0) / valid.length;
};

const calcMaxMonth = (values, months) => {
  const pairs = (values || [])
    .map((v, i) => ({ value: toNumber(v), month: months[i] || MONTHS_SHORT[i] || "-" }))
    .filter((x) => x.value != null);
  if (!pairs.length) return { label: "N/A" };
  const max = pairs.reduce((a, b) => (b.value > a.value ? b : a));
  return { label: `${max.month} - ${Math.round(max.value)} kWh` };
};

const calcMinMonth = (values, months) => {
  const pairs = (values || [])
    .map((v, i) => ({ value: toNumber(v), month: months[i] || MONTHS_SHORT[i] || "-" }))
    .filter((x) => x.value != null);
  if (!pairs.length) return { label: "N/A" };
  const min = pairs.reduce((a, b) => (b.value < a.value ? b : a));
  return { label: `${min.month} - ${Math.round(min.value)} kWh` };
};

const getPdfImageType = (imageSource) => {
  if (typeof imageSource === "string" && imageSource.startsWith("data:image/jpeg")) {
    return "JPEG";
  }
  return "PNG";
};

const drawHeader = (
  doc,
  { pageWidth, left, right, addressLine, projectName, logoDataUrl, logoPlacement }
) => {
  const headerHeight = 90;
  const topOffset = -8;
  doc.setFillColor(...colors.bg);
  doc.rect(0, 0, pageWidth, 792, "F");

  try {
    const logoSource = logoDataUrl || sflLogo;
    const placement = logoPlacement || { x: left, y: 50 + topOffset, width: 111, height: 20 };
    doc.addImage(
      logoSource,
      getPdfImageType(logoSource),
      placement.x,
      placement.y,
      placement.width,
      placement.height
    );
  } catch (e) {
    // Keep report generation resilient if image decoding fails.
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(...colors.text);
  doc.text(projectName || "N/A", pageWidth - right, 64 + topOffset, { align: "right" });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...colors.text);
  const addressLines = doc.splitTextToSize(addressLine || "N/A", 250).slice(0, 2);
  doc.text(addressLines, pageWidth - right, 76 + topOffset, { align: "right" });

  doc.setDrawColor(196, 202, 208);
  doc.setLineWidth(1);
  doc.line(left, headerHeight, pageWidth - right, headerHeight);
};

const drawFooter = (
  doc,
  {
    page,
    pageCount,
    pageWidth,
    pageHeight,
    left,
    right,
    jobName,
    designerName,
    organizationName,
    showOrganization,
  }
) => {
  const footerHeight = 70;
  const footerTop = pageHeight - footerHeight;
  const footerLineY = footerTop;
  doc.setDrawColor(205, 210, 215);
  doc.setLineWidth(1);
  doc.line(left, footerLineY, pageWidth - right, footerLineY);

  const note =
    "This report is generated using PVWatts® (NREL) data and estimates. Results may vary based on actual site conditions. All rights reserved © BambooTech LLC.";
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...colors.text);
  const wrapped = doc.splitTextToSize(note, pageWidth - left - right).slice(0, 2);
  doc.text(wrapped, pageWidth / 2, footerLineY + 10, { align: "center" });

  const rowY = footerTop + 53;
  try {
    doc.addImage(sflLogo, "PNG", left, rowY - 12, 72, 13);
  } catch (e) {
    // Keep report generation resilient if image decoding fails.
  }

  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor(...colors.text);
  doc.text(`Designer: ${designerName}`, pageWidth - right, rowY, { align: "right" });
  doc.text(`Page ${page}`, pageWidth / 2, rowY, { align: "center" });

  void pageCount;
  void jobName;
  void showOrganization;
  void organizationName;
};

const drawSunGlyph = (doc, x, y, size = 50) => {
  const cx = x + size / 2;
  const cy = y + size / 2;
  const coreR = size * 0.16;
  const innerR = size * 0.28;
  const outerR = size * 0.42;
  doc.setDrawColor(...colors.accent);
  doc.setFillColor(...colors.accent);
  doc.circle(cx, cy, coreR, "F");
  for (let i = 0; i < 8; i++) {
    const a = (Math.PI * 2 * i) / 8;
    const x1 = cx + Math.cos(a) * innerR;
    const y1 = cy + Math.sin(a) * innerR;
    const x2 = cx + Math.cos(a) * outerR;
    const y2 = cy + Math.sin(a) * outerR;
    doc.setLineWidth(2);
    doc.line(x1, y1, x2, y2);
  }
};

const drawAnnualCard = (doc, { x, y, w, annualKwh, systemSizeKw, panelCount }) => {
  doc.setFillColor(...colors.deep);
  doc.roundedRect(x, y, w, 96, 16, 16, "F");
  try {
    doc.addImage(sunIcon, "PNG", x + 20, y + 22, 50, 50);
  } catch (e) {
    // Keep report generation resilient if image decoding fails.
  }

  const annualText = fmtInt(annualKwh);
  const valueX = x + 88;

  doc.setTextColor(...colors.accent);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(32);
  doc.text(annualText, valueX, y + 50);
  const annualTextW = doc.getTextWidth(annualText);

  doc.setTextColor(235, 241, 247);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  const unitX = valueX + annualTextW + 10;
  doc.text("kWh/Year", unitX, y + 50);

  doc.setFont("helvetica", "normal");
  doc.setTextColor(...colors.mutedLight);
  doc.setFontSize(14);
  const unitW = doc.getTextWidth("kWh/Year");
  const estimatedX = unitX + unitW + 22;
  doc.text("Estimated Annual Production", estimatedX, y + 50);

  const panelText =
    typeof panelCount === "number" && Number.isFinite(panelCount)
      ? `${Math.round(panelCount)}`
      : "N/A";
  let rowX = valueX;
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...colors.mutedLight);
  doc.setFontSize(14);
  const systemLabel = "System Size ";
  doc.text(systemLabel, rowX, y + 72);
  rowX += doc.getTextWidth(systemLabel);

  doc.setFont("helvetica", "normal");
  doc.setTextColor(...colors.accent);
  const systemValue = `${fmtNum(systemSizeKw, 1)} kW`;
  doc.text(systemValue, rowX, y + 72);
  rowX += doc.getTextWidth(systemValue) + 8;

  doc.setFont("helvetica", "normal");
  doc.setTextColor(235, 241, 247);
  doc.text("•", rowX, y + 72);
  rowX += 10;

  doc.setTextColor(...colors.mutedLight);
  const panelsLabel = "Panels ";
  doc.text(panelsLabel, rowX, y + 72);
  rowX += doc.getTextWidth(panelsLabel);

  doc.setFont("helvetica", "normal");
  doc.setTextColor(...colors.accent);
  doc.text(panelText, rowX, y + 72);
};

const drawRoundedBarChart = (doc, { x, y, w, h, months, values, axisStyle = "plain" }) => {
  doc.setDrawColor(...colors.cardBorder);
  doc.setLineWidth(1);
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(x, y, w, h, 20, 20, "FD");

  const labelZoneH = axisStyle === "pill" ? 56 : axisStyle === "months" ? 22 : 35;
  const chartX = x + 24;
  const chartY = y + 16;
  const chartW = w - 48;
  const chartH = h - 16 - labelZoneH;
  const validVals = (values || []).map((v) => toNumber(v) || 0);
  const maxVal = Math.max(...validVals, 1);
  const barCount = Math.max(validVals.length, 1);
  const gap = 8;
  const bw = (chartW - gap * (barCount - 1)) / barCount;

  for (let i = 0; i < barCount; i++) {
    const v = validVals[i] || 0;
    const bh = (v / maxVal) * chartH;
    const bx = chartX + i * (bw + gap);
    const by = chartY + (chartH - bh);

    doc.setFillColor(...colors.deep);
    doc.roundedRect(bx, by, bw, bh, 7, 7, "F");

    if (axisStyle !== "pill") {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(...colors.muted);
      doc.text(months[i] || MONTHS_SHORT[i] || "-", bx + bw / 2, y + h - (axisStyle === "months" ? 8 : 22), {
        align: "center",
      });

      if (axisStyle !== "months") {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(...colors.text);
        doc.text(fmtInt(v), bx + bw / 2, y + h - 9, { align: "center" });
      }
    }
  }

  if (axisStyle === "pill") {
    const itemH = 34;
    const itemY = y + h - itemH - 10;
    for (let i = 0; i < barCount; i++) {
      const bx = chartX + i * (bw + gap);
      doc.setDrawColor(...colors.cardBorder);
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(bx, itemY, bw, itemH, 8, 8, "FD");

      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(...colors.muted);
      doc.text(months[i] || MONTHS_SHORT[i] || "-", bx + bw / 2, itemY + 12, {
        align: "center",
      });

      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(...colors.text);
      doc.text(fmtInt(validVals[i]), bx + bw / 2, itemY + 27, { align: "center" });
    }
  }
};

const drawMetricCards = (doc, { x, y, w, values }) => {
  const cardW = (w - 18) / 4;
  const titles = [
    "Highest Monthly Consumption",
    "Lowest Monthly Consumption",
    "Average Monthly Consumption",
    "Average Solar Radiation",
  ];

  for (let i = 0; i < 4; i++) {
    const cx = x + i * (cardW + 6);
    doc.setDrawColor(...colors.cardBorder);
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(cx, y, cardW, 84, 14, 14, "FD");

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(...colors.mutedLight);
    const titleLines = doc.splitTextToSize(titles[i], cardW - 16);
    doc.text(titleLines, cx + cardW / 2, y + 26, { align: "center" });

    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(...colors.text);
    doc.text(values[i], cx + cardW / 2, y + 62, { align: "center" });
  }
};

const drawSpecPanel = (doc, { x, y, w, h, leftRows, rightRows }) => {
  doc.setDrawColor(...colors.cardBorder);
  doc.setFillColor(247, 248, 250);
  doc.roundedRect(x, y, w, h, 14, 14, "FD");

  const dividerX = x + w / 2;
  doc.setDrawColor(220, 224, 228);
  doc.line(dividerX, y + 10, dividerX, y + h - 10);

  const rowCount = Math.max(leftRows.length, rightRows.length, 1);
  const topPad = 24;
  const bottomPad = 18;
  const rowGap = rowCount > 1 ? (h - topPad - bottomPad) / (rowCount - 1) : 0;

  leftRows.forEach((r, idx) => {
    const rowY = y + topPad + idx * rowGap;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(12);
    doc.setTextColor(...colors.muted);
    doc.text(r.label, x + 20, rowY);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(12);
    doc.setTextColor(...colors.text);
    doc.text(r.value, dividerX - 16, rowY, { align: "right" });
  });

  rightRows.forEach((r, idx) => {
    const rowY = y + topPad + idx * rowGap;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(12);
    doc.setTextColor(...colors.muted);
    doc.text(r.label, dividerX + 20, rowY);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(12);
    doc.setTextColor(...colors.text);
    doc.text(r.value, x + w - 20, rowY, { align: "right" });
  });
};

const drawLossRows = (doc, { x, y, w, rows, totalLoss, panelHeight = 274 }) => {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.setTextColor(...colors.text);
  doc.text("System Losses Breakdown", x, y);
  doc.text(`Total: ${fmtPct(totalLoss, 2)}`, x + w, y, { align: "right" });

  const panelY = y + 14;
  doc.setDrawColor(...colors.cardBorder);
  doc.setFillColor(247, 248, 250);
  doc.roundedRect(x, panelY, w, panelHeight, 14, 14, "FD");

  const topPad = 26;
  const bottomPad = 26;
  const contentH = Math.max(0, panelHeight - topPad - bottomPad);
  const rowGap = rows.length > 1 ? contentH / (rows.length - 1) : 0;
  const maxScale = 8;
  rows.forEach((row, idx) => {
    const ry = panelY + topPad + idx * rowGap;
    const pct = toNumber(row.value) || 0;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(12);
    doc.setTextColor(...colors.text);
    doc.text(row.label, x + 20, ry);
    doc.text(`${fmtNum(pct, 1)}%`, x + w - 20, ry, { align: "right" });

    const barY = ry + 7;
    const barX = x + 20;
    const barW = w - 40;

    doc.setFillColor(209, 213, 217);
    doc.roundedRect(barX, barY, barW, 5, 2, 2, "F");

    doc.setFillColor(...colors.deepSoft);
    doc.roundedRect(barX, barY, Math.min(barW, (pct / maxScale) * barW), 5, 2, 2, "F");
  });
};

const drawIrradianceRow = (doc, { x, y, w, values }) => {
  doc.setDrawColor(...colors.cardBorder);
  doc.setFillColor(247, 248, 250);
  doc.roundedRect(x, y, w, 40, 12, 12, "FD");

  const itemW = w / 12;
  doc.setDrawColor(220, 224, 228);
  doc.setLineWidth(0.6);
  for (let i = 1; i < 12; i++) {
    const lx = x + itemW * i;
    doc.line(lx, y + 6, lx, y + 34);
  }

  for (let i = 0; i < 12; i++) {
    const cx = x + itemW * i + itemW / 2;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(...colors.muted);
    doc.text(MONTHS_SHORT[i], cx, y + 15, { align: "center" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(...colors.text);
    doc.text(fmtPct(values?.[i], 0), cx, y + 28, { align: "center" });
  }
};

const drawKpiBoxes = (doc, { x, y, w, items }) => {
  const boxW = (w - 25) / 6;
  const boxH = 50;
  for (let i = 0; i < 6; i++) {
    const bx = x + i * (boxW + 5);
    const it = items[i] || { label: "-", value: "-" };

    doc.setDrawColor(...colors.cardBorder);
    doc.setFillColor(238, 242, 246);
    doc.roundedRect(bx, y, boxW, boxH, 10, 10, "FD");

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(...colors.muted);
    const lines = doc.splitTextToSize(it.label, boxW - 10).slice(0, 2);
    doc.text(lines, bx + 12, y + 21);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(...colors.text);
    doc.text(it.value, bx + 12, y + 34);
  }
};

export const generatePvwattsReport = async (params) => {
  const {
    MONTHS = MONTHS_SHORT,
    monthlyProduction = [],
    solarRadiationMonthly = [],
    totalProduction,
    totalLoss,
    currentDesign,
    latitude,
    longitude,
    dcSystemSizeKw,
    totalPanels = null,
    arrayTilt,
    arrayAzimuth,
    requestedLocation,
    moduleType,
    arrayType,
    dcToAcRatio,
    inverterEfficiency,
    groundCoverageRatio,
    albedo,
    bifacial,
    monthlyIrradianceLoss = [],
    dcCapacityFactor,
    lossBreakdown = {},
    segmentProductions = [],
    segmentRequestParams = [],
    segmentMonthlyIrradianceLoss = [],
    auth = {},
    project = {},
    shouldDownload = true,
  } = params;

  const doc = new jsPDF("p", "pt", "letter");
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const left = 30;
  const right = 30;
  const contentW = pageWidth - left - right;

  const addressLine = getAddress(requestedLocation, currentDesign);
  const projectName = pickFirstNonEmpty(
    currentDesign?.name,
    currentDesign?.projectName,
    currentDesign?.project_name,
    currentDesign?.title
  ) || "N/A";
  const designerName = getDesignerName(currentDesign);
  const organizationName = getOrganizationName(currentDesign);
  const jobName = getJobName(currentDesign);
  const organizationLogoUrl = getPreferredOrganizationLogoUrl(currentDesign);
  const rawOrganizationLogoDataUrl = await loadImageAsDataUrl(organizationLogoUrl);
  const organizationLogoDataUrl = rawOrganizationLogoDataUrl
    ? await trimImageWhitespace(rawOrganizationLogoDataUrl)
    : "";
  const logoSource = organizationLogoDataUrl || sflLogo;
  const logoDimensions = await loadImageDimensions(logoSource);
  const headerLogoPlacement = fitImageContain(logoDimensions, left, 37, 166.5, 30, {
    alignX: "left",
  });

  const drawPageOne = () => {
    const p1Left = left;
    const p1Right = right;
    const p1ContentW = pageWidth - p1Left - p1Right;
    drawHeader(doc, {
      pageWidth,
      left: p1Left,
      right: p1Right,
      addressLine,
      projectName,
      logoDataUrl: organizationLogoDataUrl,
      logoPlacement: headerLogoPlacement,
    });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(12);
    doc.setTextColor(...colors.text);
    doc.text("Annual Production Report", p1Left, 128);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    const addressLines = doc.splitTextToSize(addressLine, p1ContentW).slice(0, 2);
    const pageOneAddressY = 156;
    const pageOneAddressOverflowY = Math.max(0, addressLines.length - 1) * 22;
    doc.text(addressLines, p1Left, pageOneAddressY);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(14);
    doc.setTextColor(...colors.mutedLight);
    doc.text(
      `Latitude ${fmtNum(latitude, 2)} N . Longitude ${fmtNum(longitude, 2)} W`,
      p1Left,
      182 + pageOneAddressOverflowY
    );

    drawAnnualCard(doc, {
      x: p1Left,
      y: 206 + pageOneAddressOverflowY,
      w: p1ContentW,
      annualKwh: totalProduction,
      systemSizeKw: dcSystemSizeKw,
      panelCount: totalPanels,
    });

    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(...colors.text);
    doc.text("Monthly Energy Production", p1Left, 344 + pageOneAddressOverflowY);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(12);
    doc.setTextColor(...colors.mutedLight);
    doc.text("AC Energy Output (kWh)", pageWidth - p1Right, 344 + pageOneAddressOverflowY, {
      align: "right",
    });

    drawRoundedBarChart(doc, {
      x: p1Left,
      y: 362 + pageOneAddressOverflowY,
      w: p1ContentW,
      h: 238,
      months: MONTHS_SHORT,
      values: monthlyProduction,
      axisStyle: "pill",
    });

    const highest = calcMaxMonth(monthlyProduction, MONTHS);
    const lowest = calcMinMonth(monthlyProduction, MONTHS);
    const avgProd = calcAvg(monthlyProduction);
    const avgSol = calcAvg(solarRadiationMonthly);

    drawMetricCards(doc, {
      x: p1Left,
      y: 612 + pageOneAddressOverflowY,
      w: p1ContentW,
      values: [
        highest.label,
        lowest.label,
        avgProd == null ? "N/A" : `${Math.round(avgProd)} kWh`,
        avgSol == null ? "N/A" : `${avgSol.toFixed(1)} kWh/m/day`,
      ],
    });
  };

  const drawPageTwo = () => {
    doc.addPage();
    drawHeader(doc, {
      pageWidth,
      left,
      right,
      addressLine,
      projectName,
      logoDataUrl: organizationLogoDataUrl,
      logoPlacement: headerLogoPlacement,
    });

    const sectionGap = 10;
    const headingTopMargin = 18;
    const specHeadingY = 96 + headingTopMargin;
    const specY = specHeadingY + 14;
    const specH = 110;
    const lossHeadingY = specY + specH + sectionGap + headingTopMargin;
    const lossPanelHeight = 350;
    const irrHeadingY = lossHeadingY + 14 + lossPanelHeight + sectionGap + headingTopMargin;
    const irrRowY = irrHeadingY + 14;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(...colors.text);
    doc.text("System Specifications", left, specHeadingY);

    const leftRows = [
      { label: "Module Type", value: MODULE_TYPE_MAP[moduleType] || "N/A" },
      { label: "Array Type", value: ARRAY_TYPE_MAP[arrayType] || "N/A" },
      { label: "DC to AC Ratio", value: fmtNum(dcToAcRatio, 1) },
      { label: "Inverter Efficiency", value: fmtPct(inverterEfficiency, 0) },
    ];

    const rightRows = [
      { label: "Ground Coverage Ratio", value: fmtNum(groundCoverageRatio, 1) },
      { label: "Total System Losses", value: fmtPct(totalLoss, 2) },
      { label: "DC Capacity Factor", value: fmtPct(dcCapacityFactor, 1) },
      { label: "Weather Station", value: fmtLatLng(latitude, longitude) },
    ];

    drawSpecPanel(doc, {
      x: left,
      y: specY,
      w: contentW,
      h: specH,
      leftRows,
      rightRows,
    });

    const losses = [
      { label: "Module Nameplate", value: lossBreakdown.moduleNameplateRating },
      { label: "Mismatch", value: lossBreakdown.mismatch },
      { label: "Connections", value: lossBreakdown.connections },
      { label: "Light-induced Degradation", value: lossBreakdown.lightInducedDegradation },
      { label: "Wiring", value: lossBreakdown.wiring },
      { label: "Soiling", value: lossBreakdown.soiling },
      { label: "Availability", value: lossBreakdown.availability },
      { label: "Age", value: lossBreakdown.age },
      { label: "Snow", value: lossBreakdown.snow },
      { label: "Other", value: lossBreakdown.other },
    ];

    drawLossRows(doc, {
      x: left,
      y: lossHeadingY,
      w: contentW,
      rows: losses,
      totalLoss,
      panelHeight: lossPanelHeight,
    });

    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(...colors.text);
    doc.text("Monthly Irradiance Loss", left, irrHeadingY);

    drawIrradianceRow(doc, {
      x: left,
      y: irrRowY,
      w: contentW,
      values: monthlyIrradianceLoss,
    });
  };

  const drawSegmentPages = () => {
    if (!Array.isArray(segmentProductions) || !segmentProductions.length) return;

    segmentProductions.forEach((segProd, idx) => {
      doc.addPage();
      const projectId = pickFirstNonEmpty(currentDesign?.id, currentDesign?.project_id);
      const segmentAddress = projectId ? `${addressLine} - ${projectId}` : addressLine;
      drawHeader(doc, {
        pageWidth,
        left,
        right,
        addressLine: segmentAddress,
        projectName,
        logoDataUrl: organizationLogoDataUrl,
        logoPlacement: headerLogoPlacement,
      });

      const segLabel = `Section ${String.fromCharCode(65 + idx)}`;
      const outputs = segProd?.production?.outputs || {};
      const inputs = segProd?.production?.inputs || {};
      const req = segmentRequestParams[idx] || {};

      const numOr = (...vals) => {
        for (const v of vals) {
          const n = toNumber(v);
          if (n != null) return n;
        }
        return null;
      };

      const segDc = numOr(inputs.system_capacity, req.capacity);
      const segAnnual = numOr(outputs.ac_annual);
      const segTilt = numOr(inputs.tilt, req.tilt, arrayTilt);
      const segAz = numOr(inputs.azimuth, req.azimuth, arrayAzimuth);
      const segLoss = numOr(inputs.losses, req.losses, totalLoss);
      const segDcAc = numOr(inputs.dc_ac_ratio, req.dc_ac_ratio, dcToAcRatio);
      const segInvEff = numOr(inputs.inv_eff, req.inv_eff, inverterEfficiency);
      const segAlb = numOr(inputs.albedo, req.albedo, albedo);
      const segBif = numOr(
        inputs.bifaciality,
        inputs.bifacial,
        req.bifaciality,
        req.bifacial,
        bifacial
      );
      const segIrrLossAvg = calcAvg(segmentMonthlyIrradianceLoss?.[idx] || []);
      const segSolarPct = segIrrLossAvg == null ? null : 100 - segIrrLossAvg;

      const segHeadingY = 122;
      const estLabelY = 122;
      const estValueY = 146;
      const segMetaY = estValueY;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(20);
      doc.setTextColor(...colors.text);
      doc.text(segLabel, left, segHeadingY);

      const drawMetaPair = (startX, label, value) => {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        doc.setTextColor(...colors.muted);
        const labelText = `${label}: `;
        doc.text(labelText, startX, segMetaY);
        const labelW = doc.getTextWidth(labelText);

        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.setTextColor(...colors.text);
        doc.text(value, startX + labelW, segMetaY);

        return labelW + doc.getTextWidth(value);
      };

      let metaX = left;
      metaX += drawMetaPair(metaX, "DC System Size", segDc == null ? "N/A" : `${segDc.toFixed(2)} kW`) + 10;
      metaX += drawMetaPair(
        metaX,
        "Avg Solar Radiation",
        segSolarPct == null ? "N/A" : fmtPct(segSolarPct, 1)
      ) + 10;
      drawMetaPair(metaX, "Inverter Efficiency", fmtPct(segInvEff, 1));

      doc.setFont("helvetica", "normal");
      doc.setFontSize(12);
      doc.setTextColor(...colors.muted);
      doc.text("Estimated Annual Output", pageWidth - right, estLabelY, { align: "right" });

      doc.setFont("helvetica", "bold");
      doc.setFontSize(20);
      doc.setTextColor(...colors.text);
      doc.text(`${fmtInt(segAnnual)} kWh`, pageWidth - right, estValueY, { align: "right" });

      drawKpiBoxes(doc, {
        x: left,
        y: 166,
        w: contentW,
        items: [
          { label: "Losses", value: fmtPct(segLoss, 1) },
          { label: "Array Tilt", value: fmtInt(segTilt) },
          { label: "Azimuth", value: fmtInt(segAz) },
          { label: "DC to AC", value: fmtNum(segDcAc, 1) },
          { label: "Albedo", value: fmtNum(segAlb, 2) },
          { label: "Bifacial", value: segBif === 0 ? "No (0)" : segBif == null ? "N/A" : `Yes (${fmtNum(segBif, 2)})` },
        ],
      });

      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.setTextColor(...colors.text);
      doc.text("Monthly Energy Production", left, 245);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(12);
      doc.setTextColor(...colors.muted);
      doc.text("AC Energy Output (kWh)", pageWidth - right, 245, { align: "right" });

      const segmentChartHeight = 150;
      drawRoundedBarChart(doc, {
        x: left,
        y: 258,
        w: contentW,
        h: segmentChartHeight,
        months: MONTHS_SHORT,
        values: outputs.ac_monthly || [],
        axisStyle: "months",
      });

      const tableBody = MONTHS_SHORT.map((m, i) => {
        const rad = toNumber(outputs.solrad_monthly?.[i]);
        const ac = toNumber(outputs.ac_monthly?.[i]);
        const irr = toNumber(segmentMonthlyIrradianceLoss?.[idx]?.[i]);
        return [
          m,
          rad == null ? "N/A" : rad.toFixed(2),
          ac == null ? "N/A" : `${Math.round(ac)}`,
          irr == null ? "N/A" : `${Math.round(irr)}%`,
        ];
      });

      tableBody.push([
        "Annual",
        calcAvg(outputs.solrad_monthly || []) == null
          ? "N/A"
          : calcAvg(outputs.solrad_monthly || []).toFixed(2),
        fmtInt(segAnnual),
        calcAvg(segmentMonthlyIrradianceLoss?.[idx] || []) == null
          ? "N/A"
          : `${Math.round(calcAvg(segmentMonthlyIrradianceLoss?.[idx] || []))}%`,
      ]);

      const tableStartY = 258 + segmentChartHeight + 16;
      const tableStartPage = doc.internal.getCurrentPageInfo().pageNumber;
      autoTable(doc, {
        startY: tableStartY,
        margin: { left, right },
        tableWidth: contentW,
        theme: "striped",
        head: [["Month", "Solar Radiation", "AC Energy (kWh)", "Irradiance Loss"]],
        body: tableBody,
        styles: {
          font: "helvetica",
          fontSize: 10,
          cellPadding: { top: 4, right: 3, bottom: 4, left: 3 },
          textColor: [30, 42, 54],
          lineColor: [255, 255, 255],
          lineWidth: 0,
          fontStyle: "normal",
        },
        headStyles: {
          fillColor: false,
          textColor: [255, 255, 255],
          fontSize: 10,
          fontStyle: "bold",
          lineWidth: 0,
          halign: "center",
        },
        bodyStyles: {
          fontSize: 10,
          fontStyle: "normal",
          lineWidth: 0,
          fillColor: [255, 255, 255],
          halign: "center",
        },
        alternateRowStyles: {
          fillColor: [246, 248, 250],
        },
        didParseCell: (data) => {
          if (data.section === "body" && data.row.index === tableBody.length - 1) {
            data.cell.styles.fontStyle = "bold";
            data.cell.styles.fontSize = 12;
            data.cell.styles.fillColor = false;
          }
        },
        willDrawCell: (data) => {
          if (data.section === "head" && data.column.index === 0) {
            const { y, height } = data.cell;
            doc.setFillColor(...colors.deep);
            // Draw a rounded band first, then square its bottom so only top corners remain rounded.
            doc.roundedRect(left, y, contentW, height, 10, 10, "F");
            doc.rect(left, y + height - 10, contentW, 10, "F");
          }
          if (
            data.section === "body" &&
            data.row.index === tableBody.length - 1 &&
            data.column.index === 0
          ) {
            const { y, height } = data.cell;
            doc.setFillColor(232, 235, 239);
            // Draw a rounded band, then square its top so only bottom corners remain rounded.
            doc.roundedRect(left, y, contentW, height, 10, 10, "F");
            doc.rect(left, y, contentW, 10, "F");
          }
        },
        didDrawPage: (data) => {
          const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
          const topY = currentPage === tableStartPage ? tableStartY : data.settings.margin.top;
          const boxHeight = data.cursor.y - topY;
          if (boxHeight > 0) {
            doc.setDrawColor(...colors.cardBorder);
            doc.setLineWidth(1);
            doc.roundedRect(left, topY, contentW, boxHeight, 10, 10, "S");
          }
        },
      });

      const nextY = (doc.lastAutoTable?.finalY || 640) + 8;
      const fitsIrradianceRow = nextY + 78 < pageHeight - 96;
      if (fitsIrradianceRow) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(13);
        doc.setTextColor(...colors.text);
        doc.text("Monthly Irradiance Loss", left, nextY + 22);

        drawIrradianceRow(doc, {
          x: left,
          y: nextY + 30,
          w: contentW,
          values: segmentMonthlyIrradianceLoss?.[idx] || [],
        });
      }
    });
  };

  drawPageOne();
  drawPageTwo();
  drawSegmentPages();

  const pageCount = doc.internal.getNumberOfPages();
  for (let p = 1; p <= pageCount; p++) {
    doc.setPage(p);
    drawFooter(doc, {
      page: p,
      pageCount,
      pageWidth,
      pageHeight,
      left,
      right,
      jobName,
      designerName,
      organizationName,
      showOrganization: p >= 3,
    });
  }

  const fileName = "PV_Production_Report.pdf";
  const pdfArrayBuffer = doc.output("arraybuffer");
  const pdfBlob = new Blob([pdfArrayBuffer], { type: "application/pdf" });

  await uploadDesignerReport({
    token: auth?.token || "",
    projectId: project?.id || "",
    reportType: "electricity",
    fileBlob: pdfBlob,
    fileName,
  });

  if (shouldDownload !== false) {
    doc.save(fileName);
  }
};
